import { DOG_CARD_GRID_COLS_CLASS } from "@/components/shared/dogCardGrid";
import type { ReactNode } from "react";
import { DogCard } from "@/features/listings";
import { ProviderListingsSection } from "../ProviderListingsSection";
import {
    type ProviderDogListingCard,
    providerSummaryToCard,
} from "../provider-listing-card";
import { ProfileEmptyState } from "@/components/shared";
import type { CollectionWagsListing } from "@/features/listings";

function mapProviderListingToAnimalCard(
    listing: CollectionWagsListing,
    index: number,
    fallbackImageUrl: string | undefined,
): ProviderDogListingCard {
    return providerSummaryToCard(listing, {
        fallbackName: `Dog ${index + 1}`,
        fallbackImageUrl,
    });
}

function AnimalCardWithBadge({
    dog,
    priority,
}: {
    dog: ProviderDogListingCard;
    priority?: boolean;
}) {
    const isChangedStatus = Boolean(dog.status && dog.status !== "available");
    return (
        <div className="relative">
            {dog.isNew && !isChangedStatus && (
                <div className="absolute left-3 top-3 z-10 flex items-center gap-1 rounded-label bg-true-white px-2.5 py-1">
                    <span className="text-body-small font-medium text-doberman-black">
                        New
                    </span>
                </div>
            )}
            <DogCard
                measurementSurface="provider"
                id={dog.id}
                name={dog.name}
                imageUrl={dog.imageUrl}
                breedNames={dog.breedNames}
                status={dog.status}
                priceRange={dog.priceRange}
                variant="overlay"
                priority={priority}
            />
        </div>
    );
}

export function AvailableAnimalsSection({
    animalCards,
    providerId,
    totalAnimalCount,
    availableCount,
    fallbackImageUrl,
    mobileDescription,
}: {
    animalCards: ProviderDogListingCard[];
    providerId: string;
    totalAnimalCount: number;
    availableCount: number;
    fallbackImageUrl?: string;
    mobileDescription?: ReactNode;
}) {
    return (
        <ProviderListingsSection
            initialCards={animalCards}
            providerId={providerId}
            totalCount={totalAnimalCount}
            availableCount={availableCount}
            fallbackImageUrl={fallbackImageUrl}
            mobileDescription={mobileDescription}
            mobileTitle="Available dogs"
            viewAllTitle="Available Dogs"
            searchPlaceholder="Search dogs by name or breed"
            mapListingToCard={mapProviderListingToAnimalCard}
            renderCard={(dog, index = Number.POSITIVE_INFINITY, surface) => (
                <AnimalCardWithBadge
                    dog={dog}
                    priority={surface === "mobile" && index === 0}
                />
            )}
            desktopGridClassName={DOG_CARD_GRID_COLS_CLASS}
            emptyState={
                <ProfileEmptyState
                    title="No dogs listed right now"
                    description="This shelter doesn't have any dogs listed at the moment. Check back soon, or look at similar shelters nearby."
                />
            }
        />
    );
}
